/**
 * Field renderers for the PO Line Detail tab.
 *
 * Split out of POLineDetailTab.tsx, which passed the repository's 500-line
 * limit once the CR-4711 redesign landed. They are presentational and hold no
 * knowledge of the tab's layout, so they move cleanly.
 */
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../lib/api';
import type { POLineDocumentRef } from '../../lib/api';

// CR-4711: Read-only field renderer used throughout the redesigned Detail
// tab. The red borders that previously marked customer-only fields were a
// spec annotation, not a UI styling instruction (CR-4711 round 5 / Sarah
// 19/05) — visibility is enforced by the `isCustomer` gates at the call
// sites; all fields render with the standard border.
interface ReadOnlyFieldProps {
  label: string;
  value: string | undefined | null;
  badge?: number | null;
  withCalendarIcon?: boolean;
  primaryLabel?: boolean;
  /**
   * CR-7266: makes the value itself actionable, for a field that identifies
   * something the user can go and look at. Ignored when there is no value --
   * the dash must stay inert, since a field showing "-" has nothing to open.
   */
  onValueClick?: () => void;
  /** Tooltip for the actionable value. Only used alongside onValueClick. */
  valueTitle?: string;
  /**
   * CR-7275: colour for the value itself, where the value carries a verdict
   * (an order price against its contract). Empty string means no colour --
   * a field with nothing to compare must stay neutral rather than pick a
   * default, so this is deliberately not optional-with-a-fallback-colour.
   */
  valueClassName?: string;
}

export function ReadOnlyField({
  label, value, badge, withCalendarIcon, primaryLabel, onValueClick, valueTitle,
  valueClassName = '',
}: ReadOnlyFieldProps) {
  const hasValue = Boolean(value && value !== '');
  const display = hasValue ? value : '-';
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${primaryLabel ? 'text-primary' : 'text-muted-foreground'}`}>{label}</label>
      <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted/30">
        {withCalendarIcon && <Calendar className="h-4 w-4 text-muted-foreground" />}
        {onValueClick && hasValue ? (
          <button
            type="button"
            onClick={onValueClick}
            title={valueTitle}
            className="flex-1 min-w-0 text-left font-medium truncate text-primary hover:underline"
          >
            {display}
          </button>
        ) : (
          // CR-7275: these boxes are narrow and the value is truncated, which
          // matters more now that some of them carry an explanation rather
          // than a number -- "Not applicable, unit priced" does not fit. The
          // title makes the whole text reachable without changing the layout.
          <span className={`flex-1 font-medium truncate ${valueClassName}`} title={display ?? undefined}>{display}</span>
        )}
        {badge ? (
          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-semibold">{badge}</span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * CR-4711 (QA 13/08): PDF Attached and Mat Certs were read-only counts, and
 * QA asked for them to be links to the documents themselves.
 *
 * The file is fetched through the authenticated API client and handed to the
 * browser as an object URL rather than pointed at by an `<a href>`: the
 * download endpoint needs the bearer token, which a plain link would not send.
 * Each entry is a button, not an anchor, because the download is JS-driven —
 * an anchor here would need a bogus href.
 */
interface DocumentLinksFieldProps {
  label: string;
  /**
   * The documents to link to. Distinct from an empty array: `undefined` means
   * the caller could not tell us (the list serializer omits these by design),
   * `[]` means it told us there are none. See the fallback below.
   */
  documents?: POLineDocumentRef[] | null;
  count?: number | null;
}

export function DocumentLinksField({ label, documents, count }: DocumentLinksFieldProps) {
  const docs = documents ?? [];
  // Whether the caller supplied an array at all, which decides what an empty
  // field means. With an array we know the line has no documents and show a
  // dash, matching every other empty field on the tab. Without one we have
  // only the count and must show it — including a real 0, which an earlier
  // `count ? ... : '-'` swallowed because 0 is falsy, so a payload reporting
  // no documents was indistinguishable from one that reported nothing at all.
  const documentsKnown = documents != null;

  const handleDownload = async (doc: POLineDocumentRef) => {
    try {
      const response = await purchasingAPI.downloadPODocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      toast.error(`Could not download ${doc.file_name}`);
    }
  };

  const emptyDisplay = documentsKnown
    ? '-'
    : (count != null ? String(count) : '-');

  return (
    <div>
      <label className="block text-sm font-medium mb-1 text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted/30 min-h-[42px]">
        {docs.length === 0 ? (
          <span className="flex-1 font-medium truncate">{emptyDisplay}</span>
        ) : (
          <div className="flex-1 flex flex-col gap-1 min-w-0">
            {docs.map(doc => (
              <button
                key={doc.id}
                type="button"
                onClick={() => handleDownload(doc)}
                title={doc.file_name}
                className="text-left font-medium text-primary underline hover:no-underline truncate"
              >
                {doc.file_name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
